import React from 'react';
import { 
  Building2, 
  Target, 
  Database,
  FileText,
  MessageSquare,
  ChevronRight,
  ArrowUpRight,
  Calendar,
  Clock,
  CheckCircle,
  Users,
  PieChart
} from 'lucide-react';

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="border-b border-gray-200 p-4 sm:p-6">{children}</div>
);

const CardContent = ({ children }) => (
  <div className="p-4 sm:p-6">{children}</div>
);

const Overview = () => {
  return (
    <div className="space-y-6">
      {/* Phase Status Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardContent>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">Implementation Phase</dt>
                  <dd className="text-lg font-semibold text-gray-900">Vision Alignment</dd>
                  <dd className="text-sm text-gray-600">45% Complete</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">Team Size</dt>
                  <dd className="text-lg font-semibold text-gray-900">2 stakeholders</dd>
                  <dd className="text-sm text-gray-600">Key decision makers</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Database className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500">Data Assessment</dt>
                  <dd className="text-lg font-semibold text-gray-900">35,000 records</dd>
                  <dd className="text-sm text-gray-600">Initial analysis complete</dd>
                </dl>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Priority Implementation Areas */}
      <Card>
        <CardHeader>
          <h3 className="text-base font-semibold text-gray-900">Priority Implementation Areas</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-b border-gray-100 pb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Deal Flow Automation</h4>
                <span className="text-sm text-blue-600">High Priority</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Automated contact enrichment, lead scoring, and capital source matching
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '30%' }} />
              </div>
            </div>
            <div className="border-b border-gray-100 pb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Marketing Automation</h4>
                <span className="text-sm text-blue-600">Medium Priority</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Content generation, distribution, and engagement tracking
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '15%' }} />
              </div>
            </div>
            <div className="pb-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">Capital Provider Database</h4>
                <span className="text-sm text-blue-600">High Priority</span>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                Intelligent categorization and preference mapping
              </p>
              <div className="w-full bg-gray-100 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: '25%' }} />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Technical Assessment Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-gray-900">Data Infrastructure Assessment</h3>
          </CardHeader>
          <CardContent>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Contact Database Analysis</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Data quality and structure assessment complete
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Deal Flow Pipeline</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Process mapping and automation planning in progress
                  </p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-yellow-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-gray-900">Integration Requirements</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Technical architecture review ongoing
                  </p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h3 className="text-base font-semibold text-gray-900">Upcoming Milestones</h3>
          </CardHeader>
          <CardContent>
            <ul className="space-y-4">
              <li className="flex gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <Calendar className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Vision Workshop</p>
                  <p className="text-sm text-gray-500">Nov 15, 2024 - 10:00 AM EST</p>
                </div>
              </li>
              <li className="flex gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Technical Deep Dive</p>
                  <p className="text-sm text-gray-500">Nov 18-19, 2024</p>
                </div>
              </li>
              <li className="flex gap-3">
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                  <PieChart className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">Phase 1 Review</p>
                  <p className="text-sm text-gray-500">Dec 1, 2024</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Overview;